import { useMemo, useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import config from "config";
import queryString from "query-string";
import EmbedFormTemplateSettings from "./EmbedFormTemplateSettings.js";
import EmbedFormTemplateContainer from "./EmbedFormTemplateContent/EmbedFormTemplateContainer.js";
import { useCookie } from "../../../contexts/cookieContext/cookieContext.js";
import { useContent } from "../../../contexts/contentContext/contentContext.js";
import { validateSubmit } from "./Helpers/ValidationFunctions.js";
import { useLocation } from "react-router-dom";
import { DOMAIN_NAME } from "utils/globalConsts.ts";
import { useRecaptcha } from "contexts/recaptchaContext/recaptchaContext.js";
import {
  getFormDetails,
  createEmbedMailReact,
  getEmbedDynamicFormCultureByFormId,
} from "./Helpers/EmbedApiCalls.js";

import "./EmbedFormTemplate.scss";

const EmbedFormTemplate = ({
  formId,
  prevStep1 = null,
  formInEmbedPage = null,
  hideFooter = null,
  includeTitleAndDesc = null,
  embedPageFormImage = "",
  handleFinishUploadProcess = null,
  brandingColor = null,
  isPreview = null,
  embedPageEditMode = null,
  activeFormInJumboFormsPage = null,
}) => {
  const [reactCookies, setReactCookies] = useState(null);
  const location = useLocation();
  const { setShowRecaptcha } = useRecaptcha();
  const { getCookie, removeCookies, setCookies } = useCookie();
  const {
    getTextByKey,
    getPath,
    getDirection,
    getLanguage,
    getRecaptchaRequired,
    fetchEmbedPageConsts,
  } = useContent();
  const params = useParams();
  const isDemoEmbedPage = location.search.includes("dem=") ? true : false;
  const parametersOfForm = queryString.parse(location.search);
  const skipFormParam =
    parametersOfForm["skipForm"] === "1" ||
    parametersOfForm["skipForm"] === 1 ||
    parametersOfForm["skipForm"] === "true"
      ? true
      : false;
  const [typeUser, setTypeUser] = useState("free");
  const [embedJson, setEmbedJson] = useState([]);
  const [formTitle, setFormTitle] = useState("");
  const [formImage, setFormImage] = useState(null);
  const [formDescription, setFormDescription] = useState("");
  const [formDetails, setFormDetails] = useState(null);
  const [footerDetails, setFooterDetails] = useState(null);
  const [direction, setDirection] = useState(getDirection());
  const [displayFooterLinks, setDisplayFooterLinks] = useState(true);
  const [loading, setLoading] = useState(skipFormParam);
  const [loadingCulture, setLoadingCulture] = useState(false);
  const [loadingButton, setLoadingButton] = useState(false);
  const [startUpload, setStartUpload] = useState(false);
  const [finishUpload, setFinishUpload] = useState(false);
  const [reSendMail, setReSendMail] = useState(false);
  const [errorFields, setErrorFields] = useState([]);
  const [embedMailId, setEmbedMailId] = useState(null);
  const [allowUseForm, setAllowUseForm] = useState(true);
  const [prevCookies, setPrevCookies] = useState(null);
  const [allowUseFormText, setAllowUseFormText] = useState("");
  const [notExistForm, setNotExistForm] = useState(false);
  const [activeForm, setActiveForm] = useState(false); //Embed_Dynamic_Live_Status
  const [displayPreview, setDisplayPreview] = useState(false);
  const [skipForm, setSkipForm] = useState(false);
  const [embedPageBrandingColor, setEmbedPageBrandingColor] = useState(null);
  const [counted, setCounted] = useState(false);
  const [embedHideImage, setEmbedHideImage] = useState(false);
  const [embedHideTitle, setEmbedHideTitle] = useState(false);
  const [embedHideDescription, setEmbedHideDescription] = useState(false);
  const [displayImageForm, setDisplayImageForm] = useState(true);
  const [displayTitleForm, setDisplayTitleForm] = useState(true);
  const [displayDescriptionForm, setDisplayDescriptionForm] = useState(true);
  const [hideDetails, setHideDetails] = useState(null);
  const [tidParam, setTidParam] = useState("");
  const [innerScrollHeight, setInnerScrollHeight] = useState(false);
  const [maxFormHeight, setMaxFormHeight] = useState(0);
  const [isPickerForm, setIsPickerForm] = useState(false);
  //Design of the Form:
  const [height, setHeight] = useState(515);
  const [width, setWidth] = useState(460);
  const [buttonBackgroundColor, setButtonBackgroundColor] = useState(null);
  const [buttonTextColor, setButtonTextColor] = useState(null);
  const [backgroundFormColor, setBackgroundFormColor] = useState(null);

  //Set the branding colof of the embed form
  useMemo(() => {
    if (brandingColor) {
      if (!brandingColor.startsWith("#")) {
        setEmbedPageBrandingColor("#" + brandingColor);
      } else {
        setEmbedPageBrandingColor(brandingColor);
      }
    }
  }, [brandingColor]);

  //If user hide image display the image only in embed page
  useEffect(() => {
    if (!formInEmbedPage) {
      if (embedHideImage) {
        setDisplayImageForm(false);
      } else {
        setDisplayImageForm(true);
      }
    } else {
      setDisplayImageForm(true);
    }
  }, [formInEmbedPage, embedHideImage]);

  //If user hide title display the title only in embed page
  useEffect(() => {
    if (!formInEmbedPage) {
      if (embedHideTitle) {
        setDisplayTitleForm(false);
      } else {
        setDisplayTitleForm(true);
      }
    } else {
      setDisplayTitleForm(true);
    }
  }, [formInEmbedPage, embedHideTitle]);

  //If user hide description display the description only in embed page
  useEffect(() => {
    if (!formInEmbedPage) {
      if (embedHideDescription) {
        setDisplayDescriptionForm(false);
      } else {
        setDisplayDescriptionForm(true);
      }
    } else {
      setDisplayDescriptionForm(true);
    }
  }, [formInEmbedPage, embedHideDescription]);

  useEffect(() => {
    let hideAllDetails = false;
    let hideDesc = false;
    let hideTitle = false;
    let hideImage = false;
    if (!formInEmbedPage) {
      if (embedHideDescription || !formDescription) {
        hideDesc = true;
      }

      if (!formTitle || embedHideTitle) {
        hideTitle = true;
      }

      if (embedHideImage || !formImage) {
        hideImage = true;
      }

      if (hideDesc && hideTitle && hideImage) {
        hideAllDetails = true;
      }

      setHideDetails(hideAllDetails);
    }
  }, [
    formInEmbedPage,
    embedHideDescription,
    embedHideTitle,
    embedHideImage,
    formDescription,
    formImage,
    formTitle,
  ]);

  const baseUrl = `https://${DOMAIN_NAME}`;

  const [linkFooters, setLinkFooters] = useState([
    {
      FooterLinkTitle: getTextByKey("about-footer"),
      FooterLinkUrl: baseUrl + getPath() + "jumbo-forms",
    },
    {
      FooterLinkTitle: getTextByKey("support-footer"),
      FooterLinkUrl: baseUrl + getPath() + "support/jumbo-embed",
    },
    {
      FooterLinkTitle: getTextByKey("terms-footer"),
      FooterLinkUrl: baseUrl + getPath() + "terms",
    },
  ]);

  const searchParams =
    typeUser === "premium" ? queryString.parse(location.search) : null;

  useEffect(() => {
    if (searchParams) {
      setTidParam(searchParams["tid"] !== undefined ? searchParams["tid"] : "");
      setSkipForm(
        searchParams["skipForm"] === "1" ||
          searchParams["skipForm"] === 1 ||
          searchParams["skipForm"] === "true"
          ? true
          : false
      );
    }
  }, [searchParams]);

  const handleCreateMailResend = async (captchaResponse) => {
    if (!captchaResponse && getRecaptchaRequired()) {
      setShowRecaptcha("global", handleCreateMailResend);
      return;
    }
    setShowRecaptcha("global", null);
    if (reSendMail) {
      if (typeUser === "premium") {
        if (skipForm && embedJson.length > 0) {
          let newJson = embedJson;
          newJson.map((item, i) =>
            searchParams["field" + i] !== undefined
              ? (item.TextValue = searchParams["field" + i])
              : (item.TextValue = "")
          );
          const leads = validateSubmit(newJson);
          const embedFormId = formId ? formId : formDetails.Embed_Dynamic_Id;
          if (leads.status === "error") {
            setErrorFields(leads.list);
          } else {
            const createRes = await createEmbedMailReact({
              embedFormId: embedFormId,
              embedJsonLeads: leads.list,
              setStartUpload: setStartUpload,
              setEmbedMailId: setEmbedMailId,
              setReactCookies: setReactCookies,
              setLoadingButton: setLoadingButton,
              EmbedExternalTransactionId:
                searchParams["tid"] !== undefined ? searchParams["tid"] : "",
              setLoading: setLoading,
              skipForm: skipForm,
              recaptchaResponse: captchaResponse,
            });
            if (createRes.status === "CaptchaRequired") {
              setShowRecaptcha("global", handleCreateMailResend);
            }
          }
        }
      }
      setReSendMail(false);
    }
  };

  useEffect(() => {
    handleCreateMailResend();
  }, [reSendMail]);

  //Reset form
  const resetForm = () => {
    setEmbedJson([]);
    setErrorFields([]);
    setPrevCookies(null);
    setFormDetails(null);
    setEmbedMailId(null);
    setFooterDetails(null);
    setFormTitle(null);
    setFormImage(null);
    setFormDescription(null);
    setStartUpload(false);
    setFinishUpload(false);
    setActiveForm(true);
    setAllowUseForm(true);
    setNotExistForm(false);
    setAllowUseFormText("");
    if (handleFinishUploadProcess) {
      handleFinishUploadProcess(false);
    }
  };
  const returnLongCulture = (culture) => {
    let longCulture = "en-US";
    switch (culture) {
      case "en":
        longCulture = "en-US";
        break;
      case "es":
        longCulture = "es-ES";
        break;
      case "it":
        longCulture = "it-IT";
        break;
      case "ru":
        longCulture = "ru-RU";
        break;
      case "he":
        longCulture = "he-IL";
        break;
      case "ar":
        longCulture = "ar-SA";
        break;
      default:
        longCulture = "en-US";
        break;
    }

    return longCulture;
  };
  //Get form details
  useMemo(async () => {
    if (
      (params.EmbedDynamicId !== undefined && params.EmbedDynamicId > 0) ||
      formId > 0
    ) {
      if (formDetails) {
        resetForm();
      }
      setLoading(true);
      if (formInEmbedPage) {
        if (isDemoEmbedPage) {
          setLoadingCulture(true);

          fetchEmbedPageConsts({
            formLanguage: returnLongCulture(getLanguage()),
          });
          setTimeout(() => {
            setLoadingCulture(false);
          }, 500);
        } else {
          setLoadingCulture(true);
          const res = await getEmbedDynamicFormCultureByFormId({
            embedFormId: formId ? formId : params.EmbedDynamicId,
          });
          if (res.status === "success") {
            fetchEmbedPageConsts({
              formLanguage: returnLongCulture(res.data),
            });
            setTimeout(() => {
              setLoadingCulture(false);
            }, 500);
          }
        }
      } else {
        setLoadingCulture(true);
        const res = await getEmbedDynamicFormCultureByFormId({
          embedFormId: formId ? formId : params.EmbedDynamicId,
        });
        if (res.status === "success") {
          if (res.data === "ispicker") {
            setIsPickerForm(true);
            fetchEmbedPageConsts({
              formLanguage: returnLongCulture(getLanguage()),
            });
          } else {
            if (res.data !== getLanguage()) {
              fetchEmbedPageConsts({
                formLanguage: returnLongCulture(res.data),
              });
            }
          }

          setTimeout(() => {
            setLoadingCulture(false);
          }, 500);
        }
      }

      getFormDetails({
        formId: formId ? formId : params.EmbedDynamicId,
        setFormDetails,
        setFooterDetails,
        setLoading,
        setNotExistForm,
        prevStep1,
        setTypeUser,
      });
    }
  }, [formId]);

  //If skip form - create mail
  const handleCreateMailSkipForm = async (captchaResponse) => {
    if (!captchaResponse && getRecaptchaRequired()) {
      setShowRecaptcha("global", handleCreateMailSkipForm);
      return;
    }
    setShowRecaptcha("global", null);
    if (typeUser === "premium") {
      if (skipForm && embedJson.length > 0) {
        let newJson = embedJson;
        newJson.map((item, i) =>
          searchParams["field" + i] !== undefined
            ? (item.TextValue = searchParams["field" + i])
            : (item.TextValue = "")
        );
        const leads = validateSubmit(newJson);
        const embedFormId = formId ? formId : formDetails.Embed_Dynamic_Id;
        if (leads.status === "error") {
          setErrorFields(leads.list);
        } else {
          const createRes = await createEmbedMailReact({
            embedFormId: embedFormId,
            embedJsonLeads: leads.list,
            setStartUpload: setStartUpload,
            setEmbedMailId: setEmbedMailId,
            setReactCookies: setReactCookies,
            setLoadingButton: setLoadingButton,
            EmbedExternalTransactionId:
              searchParams["tid"] !== undefined ? searchParams["tid"] : "",
            setLoading: setLoading,
            skipForm: skipForm,
            recaptchaResponse: captchaResponse,
          });
          if (createRes.status === "CaptchaRequired") {
            setShowRecaptcha("global", handleCreateMailSkipForm);
          }
        }
      }
    }
  };

  useEffect(() => {
    handleCreateMailSkipForm();
  }, [embedJson]);

  //Create embed mail
  const createEmbedMail = async (captchaResponse) => {
    if (!captchaResponse && getRecaptchaRequired()) {
      setShowRecaptcha("global", createEmbedMail);
      return;
    }
    setShowRecaptcha("global", null);
    if (isPreview) {
      return;
    }
    if (embedPageEditMode) {
      return;
    }
    const leads = validateSubmit(embedJson);
    const embedFormId = formId ? formId : formDetails.Embed_Dynamic_Id;
    if (leads.status === "error") {
      setErrorFields(leads.list);
    } else {
      const createRes = await createEmbedMailReact({
        embedFormId: embedFormId,
        embedJsonLeads: leads.list,
        setStartUpload: setStartUpload,
        setEmbedMailId: setEmbedMailId,
        setReactCookies: setReactCookies,
        setLoadingButton: setLoadingButton,
        EmbedExternalTransactionId:
          typeUser === "premium"
            ? searchParams["tid"] !== undefined
              ? searchParams["tid"]
              : ""
            : "",
        setLoading: setLoading,
        skipForm: false,
        recaptchaResponse: captchaResponse,
      });
      if (createRes.status === "CaptchaRequired") {
        setShowRecaptcha("global", createEmbedMail);
      }
    }
  };

  //If preview mode or not
  useEffect(() => {
    if (activeForm) {
      setDisplayPreview(true);
    } else {
      if (isPreview) {
        setDisplayPreview(true);
      } else {
        setDisplayPreview(false);
      }
    }
  }, [isPreview, activeForm]);

  //Set cookies
  useEffect(() => {
    if (reactCookies && formDetails) {
      setEmbedDynamicFormCookies({
        dynamicFormId: formDetails.Embed_Dynamic_Id,
        cookieString: reactCookies,
      });
    }
  }, [reactCookies, formId, formDetails]);

  //Set cookies function
  const setEmbedDynamicFormCookies = ({ dynamicFormId, cookieString }) => {
    let cookieName = "Lead-" + dynamicFormId;
    let cookieNameTest = "Test-Lead-" + dynamicFormId;
    if (config.api.src.includes("testapi")) {
      cookieName = cookieNameTest;
    }
    let convertedCookie = "";
    if (cookieString) {
      Object.keys(cookieString).map(function (key) {
        convertedCookie += key + "=" + cookieString[key].trim();
        convertedCookie += "&";
      });
    }
    if (convertedCookie) {
      let newConverted = convertedCookie.substring(
        0,
        convertedCookie.length - 1
      );
      const getCookieConst = getCookie(cookieName.trim());
      if (getCookieConst !== undefined) {
        removeCookies(cookieName);
      }
      setCookies(cookieName, newConverted, {
        path: "/",
        domain: ".jumbomail.me",
      });
    }
  };

  return (
    <EmbedFormTemplateSettings
      activeFormInJumboFormsPage={activeFormInJumboFormsPage}
      typeUser={typeUser}
      formDetails={formDetails}
      setLoading={setLoading}
      formInEmbedPage={formInEmbedPage}
      setErrorFields={setErrorFields}
      formId={formId}
      setPrevCookies={setPrevCookies}
      setStartUpload={setStartUpload}
      setEmbedJson={setEmbedJson}
      setActiveForm={setActiveForm}
      displayPreview={displayPreview}
      setFormTitle={setFormTitle}
      setFormDescription={setFormDescription}
      embedPageFormImage={embedPageFormImage}
      setFormImage={setFormImage}
      setEmbedHideImage={setEmbedHideImage}
      setEmbedHideTitle={setEmbedHideTitle}
      setEmbedHideDescription={setEmbedHideDescription}
      setButtonBackgroundColor={setButtonBackgroundColor}
      embedPageBrandingColor={embedPageBrandingColor}
      setButtonTextColor={setButtonTextColor}
      brandingColor={brandingColor}
      setBackgroundFormColor={setBackgroundFormColor}
      setWidth={setWidth}
      setHeight={setHeight}
      setDirection={setDirection}
      setDisplayFooterLinks={setDisplayFooterLinks}
      hideFooter={hideFooter}
      footerDetails={footerDetails}
      displayFooterLinks={displayFooterLinks}
      setLinkFooters={setLinkFooters}
      counted={counted}
      setCounted={setCounted}
      setAllowUseForm={setAllowUseForm}
      setAllowUseFormText={setAllowUseFormText}
      isDemoEmbedPage={isDemoEmbedPage}
      setMaxFormHeight={setMaxFormHeight}
      setInnerScrollHeight={setInnerScrollHeight}
      skipFormParam={skipFormParam}
      isPickerForm={isPickerForm}
    >
      <EmbedFormTemplateContainer
        typeUser={typeUser}
        loadingCulture={loadingCulture}
        activeForm={activeForm}
        allowUseForm={allowUseForm}
        notExistForm={notExistForm}
        isPreview={isPreview}
        direction={direction}
        formInEmbedPage={formInEmbedPage}
        width={width}
        height={height}
        formDetails={formDetails}
        loading={loading}
        displayPreview={displayPreview}
        startUpload={startUpload}
        buttonBackgroundColor={buttonBackgroundColor}
        setStartUpload={setStartUpload}
        embedMailId={embedMailId}
        setEmbedMailId={setEmbedMailId}
        finishUpload={finishUpload}
        setFinishUpload={setFinishUpload}
        handleFinishUploadProcess={handleFinishUploadProcess}
        hideFooter={hideFooter}
        tidParam={tidParam}
        hideDetails={hideDetails}
        includeTitleAndDesc={includeTitleAndDesc}
        displayImageForm={displayImageForm}
        formImage={formImage}
        displayTitleForm={displayTitleForm}
        formTitle={formTitle}
        displayDescriptionForm={displayDescriptionForm}
        formDescription={formDescription}
        embedJson={embedJson}
        errorFields={errorFields}
        setEmbedJson={setEmbedJson}
        prevCookies={prevCookies}
        createEmbedMail={createEmbedMail}
        buttonTextColor={buttonTextColor}
        backgroundFormColor={backgroundFormColor}
        loadingButton={loadingButton}
        searchParams={searchParams}
        displayFooterLinks={displayFooterLinks}
        linkFooters={linkFooters}
        allowUseFormText={allowUseFormText}
        isDemoEmbedPage={isDemoEmbedPage}
        embedPageEditMode={embedPageEditMode}
        maxFormHeight={maxFormHeight}
        innerScrollHeight={innerScrollHeight}
        setReSendMail={setReSendMail}
        skipForm={skipForm}
      />
    </EmbedFormTemplateSettings>
  );
};

export default EmbedFormTemplate;
